import { PayloadAction, createSlice } from "@reduxjs/toolkit"

interface Banner {
  isVisible: boolean
  title: string
  content: string
  closeOnNavigation?: boolean
  onClose?: () => void
}

export enum BannerTypeEnum {
  ERROR = "error",
  INFO = "info",
  WARNING = "warning",
  SUCCESS = "success",
}
interface BannerWithId {
  id: string
  type: BannerTypeEnum
  title: string
  content: string
  closeOnNavigation?: boolean
  onClose?: () => void
}

export const initialBannerState: Banner = {
  isVisible: false,
  title: "",
  content: "",
  closeOnNavigation: true,
  onClose: undefined,
}

export interface ErrorState {
  /** @deprecated Use bannersWithId instead. */
  actionNeededBanner: Banner
  /** @deprecated Use bannersWithId instead. */
  errorBanner: Banner
  /** @deprecated Use bannersWithId instead. */
  infoBanner: Banner
  /** @deprecated Use bannersWithId instead. */
  warningBanner: Banner
  /** @deprecated Use bannersWithId instead. */
  successBanner: Banner
  bannersWithId: BannerWithId[]
}

export const initialState: ErrorState = {
  actionNeededBanner: initialBannerState,
  errorBanner: initialBannerState,
  infoBanner: initialBannerState,
  warningBanner: initialBannerState,
  successBanner: initialBannerState,
  bannersWithId: [],
}

const errorHandlerSlice = createSlice({
  name: "errorHandler",
  initialState,
  reducers: {
    errorHandlerUpdated: (
      state: ErrorState,
      { payload }: PayloadAction<Partial<ErrorState>>,
    ) => {
      state.actionNeededBanner = payload.actionNeededBanner
        ? {
            ...state.actionNeededBanner,
            ...payload.actionNeededBanner,
          }
        : state.actionNeededBanner
      state.errorBanner = payload.errorBanner
        ? {
            ...state.errorBanner,
            ...payload.errorBanner,
          }
        : state.errorBanner
      state.infoBanner = payload.infoBanner
        ? {
            ...state.infoBanner,
            ...payload.infoBanner,
          }
        : state.infoBanner
      state.warningBanner = payload.warningBanner
        ? {
            ...state.warningBanner,
            ...payload.warningBanner,
          }
        : state.warningBanner
      // The successBanner is included in this slice for consistency with other banner states.
      // Although it does not semantically fit with error handling, it controls a banner similar to the others.
      state.successBanner = payload.successBanner
        ? {
            ...state.successBanner,
            ...payload.successBanner,
          }
        : state.successBanner
      // Update banners with ID if provided - this overrides the entire list if provided
      state.bannersWithId = payload.bannersWithId
        ? payload.bannersWithId
        : state.bannersWithId
    },
    addOrUpdateBannerById: (
      state: ErrorState,
      { payload }: PayloadAction<BannerWithId>,
    ) => {
      const existingIndex = state.bannersWithId.findIndex(
        (banner) => banner.id === payload.id,
      )
      if (existingIndex !== -1) {
        // Update existing banner
        state.bannersWithId[existingIndex] = {
          ...state.bannersWithId[existingIndex],
          ...payload,
        }
      } else {
        // Add new banner
        state.bannersWithId.push(payload)
      }
    },
    removeBannersByIds: (
      state: ErrorState,
      { payload }: PayloadAction<string[]>,
    ) => {
      state.bannersWithId = state.bannersWithId.filter(
        (banner) => !payload.includes(banner.id),
      )
    },
    bannersReset: (state: ErrorState) => {
      state.actionNeededBanner = initialState.actionNeededBanner
      state.errorBanner = initialState.errorBanner
      state.infoBanner = initialState.infoBanner
      state.warningBanner = initialState.warningBanner
      state.successBanner = initialState.successBanner
    },
  },
})

export const {
  errorHandlerUpdated,
  addOrUpdateBannerById,
  removeBannersByIds,
  bannersReset,
} = errorHandlerSlice.actions

export const selectErrorHandler = (state: { errorHandler: ErrorState }) => {
  return state.errorHandler
}

export default errorHandlerSlice.reducer
